import React, { useMemo, useState } from "react";
import { AppModal } from "components/modal";
import { ModalSeriesData } from "./fluidMetrics";
import { SeriesData } from "components/lineChart/lineChart";
import { Colors } from "helpers/colors";
import { AppLineChart } from "components/lineChart";
import { CommonPatientExtensionItems } from "pages/patients/commonPatientExtensionItems";
import {
  CardTitleText,
  CountItemCountText,
  CountItemDescriptionContainer,
  CountItemTitleContainer,
  ErrorMessage,
  FluidMetricsGridContainer,
  FluidMetricsGridItem,
  FluidMetricsResponsiveGridItem,
  FluidMetricsSubtitleContainer,
  FluidMetricsTitleText,
  NoDataText,
  OverflowContainer,
  StepContainer,
  StepDataContainer,
  StepLabelText,
  StepValueText,
  ViewMoreText
} from "./style";
import { AppCard } from "components/card";
import { fromPairs, get, isArray, isEmpty, sortBy } from "lodash";
import { Skeleton, Spin, Checkbox, Modal } from "antd";
import { ExpandAltOutlined } from "@ant-design/icons";
import { Dropdown } from "components/dropDownBox";
import {
  DropdownContentContainer,
  DropdownHeaderContainer
} from "pages/providerDetails/style";
import { FeedbackForm } from "./feedbackForm";
import { FluidMetricsResponse } from "sharedTypes";
import moment from "moment";
import { createApiClient } from "apiClient";
import { FluidMetricsTable } from "./fluidMetricsTable";
import { Form, InputNumber, Row, Col, Button } from "antd";
import { flex, justifyContent } from "styled-system";

interface FluidMetricsContentInterface {
  readonly loading: boolean;
  readonly error: unknown;
  readonly data: any;
}

const calculatePercentile = (data: number[], percentile: number) => {
  const sorted = data.sort((a: number, b: number) => a - b);
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * percentile;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  } else {
    return sorted[base];
  }
};

export const FluidMetricsContent: React.FC<FluidMetricsContentInterface> = ({
  loading,
  error,
  data
}) => {
  const [isViewMoreDropdownVisible, setIsViewMoreDropdownVisible] =
    useState<boolean>(false);

  const onViewMoreDropdownVisibilityChange = (isVisible: boolean) =>
    setIsViewMoreDropdownVisible(isVisible);

  const [modalData, setModalData] = useState<ModalSeriesData>({
    data: [],
    title: "",
    colors: []
  });

  // State for deny modal
  const [isDenyModalVisible, setIsDenyModalVisible] = useState(false);
  const [denialReasons, setDenialReasons] = useState({
    lowBP: false,
    highBP: false,
    priorOrCurrentSymptoms: false,
    lowBPSymptoms: false,
    cramping: false,
    nausea: false,
    volumeOverload: false,
    swellingPuffiness: false,
    shortnessOfBreath: false,
    excessiveIDWG: false,
    patientRefusal: false,
    otherClinicalJudgement: false
  });
  const [customReasonText, setCustomReasonText] = useState("");

  const countData = useMemo(() => {
    if (!data && !isArray(data)) return [];
    return [
      {
        title: "PW-TW > 1",
        count: get(data?.[0], "num_runs_pw_tw_more_1", ""),
        description: "Numbers of times PW MORE than TW by > 1KG in last 14 runs"
      },
      {
        title: "PW-TW < -1",
        count: get(data?.[0], "num_runs_pw_tw_less_1_minus", ""),
        description: "Numbers of times PW LESS than TW by > 1KG in last 14 runs"
      },
      {
        title: "IDWG > 5%",
        count: get(data?.[0], "num_runs_idwg_more_5_per", ""),
        description: "Number of times IDWG was more than 5% in last 14 runs"
      },
      {
        title: "Avg UFR",
        count: get(data?.[0], "avg_ufr_run_6rx", ""),
        description: "Average 6 Day UFR"
      },
      {
        title: "Pre Sys",
        count: get(data?.[0], "avg_3day_pre_sys_bin", ""),
        description: "Average 3 Day Pre Systolic BP Range"
      },
      {
        title: "Post Sys",
        count: get(data?.[0], "avg_3day_post_sys_bin", ""),
        description: "Average 3 Day Post Systolic BP Range"
      },
      {
        title: "BP Drops",
        count: get(
          data?.[0],
          "num_runs_bp_drop_more_equal_50_and_avg_bp_drop_less_90",
          ""
        ),
        description: "BP drop to less than 90 in over 50% of the runs"
      },
      {
        title: "UFR to Reach TW",
        count: get(data?.[0], "ufr_to_reach_tw", ""),
        description: "UFR to Reach TW"
      }
    ];
  }, [data]);

  const weightLineChartData: SeriesData[] = useMemo(() => {
    if (!data && !isArray(data)) return [];
    return [
      {
        name: "targetWt",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.target_weight
        ])
      },
      {
        name: "postWtKg",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.post_wt_kg
        ])
      }
    ];
  }, [data]);

  const preBpLineChartData: SeriesData[] = useMemo(() => {
    if (!data && !isArray(data)) return [];
    const eightySevenPercentile = calculatePercentile(
      data.map((item: any) => item.pre_bp_systolic),
      0.87
    );
    const twelvePointFivePercentile = calculatePercentile(
      data.map((item: any) => item.pre_bp_systolic),
      0.125
    );
    return [
      {
        name: "Threshold",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          Number(item.Threshold_Pre_BP)
        ])
      },
      {
        name: "Pre BP",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.pre_bp_systolic
        ])
      },
      {
        name: "87%tile",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          eightySevenPercentile
        ]),
        dashStyle: "dash"
      },
      {
        name: "12.5%tile",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          twelvePointFivePercentile
        ]),
        dashStyle: "dash"
      }
    ];
  }, [data]);

  const postBpLineChartData: SeriesData[] = useMemo(() => {
    if (!data && !isArray(data)) return [];
    const eightySevenPercentile = calculatePercentile(
      data.map((item: any) => item.post_bp_systolic),
      0.87
    );
    const twelvePointFivePercentile = calculatePercentile(
      data.map((item: any) => item.post_bp_systolic),
      0.125
    );
    return [
      {
        name: "Threshold",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          Number(item.Threshold_Post_BP)
        ])
      },
      {
        name: "Post BP",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.post_bp_systolic
        ])
      },
      {
        name: "87%tile",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          eightySevenPercentile
        ]),
        dashStyle: "dash"
      },
      {
        name: "12.5%tile",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          twelvePointFivePercentile
        ]),
        dashStyle: "dash"
      }
    ];
  }, [data]);

  const lowBpLineChartData: SeriesData[] = useMemo(() => {
    if (!data && !isArray(data)) return [];
    const eightySevenPercentile = calculatePercentile(
      data.map((item: any) => item.lowest_bp_systolic),
      0.87
    );
    const twelvePointFivePercentile = calculatePercentile(
      data.map((item: any) => item.lowest_bp_systolic),
      0.125
    );
    return [
      {
        name: "Threshold",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          Number(item.Threshold_Low_BP)
        ])
      },
      {
        name: "Lowest BP",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.lowest_bp_systolic
        ])
      },
      {
        name: "87%tile",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          eightySevenPercentile
        ]),
        dashStyle: "dash"
      },
      {
        name: "12.5%tile",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          twelvePointFivePercentile
        ]),
        dashStyle: "dash"
      }
    ];
  }, [data]);

  const actualUFRLineChartData: SeriesData[] = useMemo(() => {
    if (!data && !isArray(data)) return [];
    return [
      {
        name: "Threshold",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          Number(item.Threshold_UFR)
        ])
      },
      {
        name: "Actual UFR",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.actual_ufr
        ])
      }
    ];
  }, [data]);

  const showModal = (title: string, data: SeriesData[], colors: string[]) => {
    setModalData({ title: title, data: data, colors });
  };

  const bloodPressureTrendsChartData: SeriesData[] = useMemo(() => {
    if (!data || !isArray(data)) return [];

    return [
      {
        name: "Pre-HD Systolic",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.pre_bp_systolic
        ])
      },
      {
        name: "Post-HD Systolic",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.post_bp_systolic
        ])
      },
      {
        name: "Lowest Intra-dialytic",
        data: sortBy(data, "run_date").map(item => [
          moment(item.run_date, "YYYY-MM-DD").valueOf(),
          item.lowest_bp_systolic
        ])
      }
    ];
  }, [data]);

  const [formData, setFormData] = useState<any>({
    preWeight: 114.4,
    postWeight: 114.0,
    targetWeight: 115.5,
    preHdSystolicBP: 126,
    postHdSystolicBP: 161,
    lowestIntraDialyticBP: 59,
    duration: 240,
    preBpSystolic: 150,
    preBpDiastolic: 90,
    postBpSystolic: 130,
    postBpDiastolic: 80,
    interDiaWtGain: 2.5,
    idwgPercentEhr: 3.6
  });

  const [selectedSymptoms, setSelectedSymptoms] = useState<any>(new Set());
  const [errors, setErrors] = useState<any>({});

  const symptoms = [
    "Hypotension",
    "Muscle Cramps",
    "Nausea",
    "Vomiting",
    "Headache",
    "Dizziness",
    "Chest Pain",
    "Shortness of Breath"
  ];

  const handleInputChange = (field: any, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value !== null && value !== undefined ? value : ""
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: any) => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleSymptomToggle = (symptom: any) => {
    const newSymptoms = new Set(selectedSymptoms);
    if (newSymptoms.has(symptom)) {
      newSymptoms.delete(symptom);
    } else {
      newSymptoms.add(symptom);
    }
    setSelectedSymptoms(newSymptoms);
  };

  const validateForm = () => {
    const newErrors: any = {};

    // Validate required numeric fields
    const requiredFields = [
      {
        key: "preWeight",
        label: "Pre-weight (kg)",
        step: "0.1",
        type: "number"
      },
      {
        key: "postWeight",
        label: "Post-weight (kg)",
        step: "0.1",
        type: "number"
      },
      {
        key: "targetWeight",
        label: "Target weight (kg)",
        step: "0.1",
        type: "number"
      },
      {
        key: "preHdSystolicBP",
        label: "Pre-HD Systolic BP",
        type: "number"
      },
      {
        key: "postHdSystolicBP",
        label: "Post-HD Systolic BP",
        type: "number"
      },
      {
        key: "lowestIntraDialyticBP",
        label: "Lowest Intra-dialytic BP",
        type: "number"
      },
      {
        key: "duration",
        label: "Duration (minutes)",
        type: "number"
      },
      {
        key: "preBpSystolic",
        label: "Pre-BP Systolic",
        type: "number"
      },
      {
        key: "preBpDiastolic",
        label: "Pre-BP Diastolic",
        type: "number"
      },
      {
        key: "postBpSystolic",
        label: "Post-BP Systolic",
        type: "number"
      },
      {
        key: "postBpDiastolic",
        label: "Post-BP Diastolic",
        type: "number"
      },
      {
        key: "interDiaWtGain",
        label: "Inter-dialytic Weight Gain (kg)",
        step: "0.1",
        type: "number"
      },
      {
        key: "idwgPercentEhr",
        label: "IDWG Percent EHR",
        step: "0.1",
        type: "number"
      }
    ];

    requiredFields.forEach((field: any) => {
      const value = formData[field.key];

      // Check if value is null, undefined, or empty string
      if (value === null || value === undefined || value === "") {
        newErrors[field.key] = `${field.label} is required`;
      } else if (isNaN(Number(value))) {
        newErrors[field.key] = `${field.label} must be a valid number`;
      } else if (Number(value) < 0) {
        newErrors[field.key] = `${field.label} must be positive`;
      }
    });

    // Validate BP ranges
    if (formData.preHdSystolicBP && !isNaN(Number(formData.preHdSystolicBP))) {
      const value = Number(formData.preHdSystolicBP);
      if (value > 300) newErrors.preHdSystolicBP = "Pre-HD BP seems too high";
    }

    if (
      formData.postHdSystolicBP &&
      !isNaN(Number(formData.postHdSystolicBP))
    ) {
      const value = Number(formData.postHdSystolicBP);
      if (value > 300) newErrors.postHdSystolicBP = "Post-HD BP seems too high";
    }

    if (
      formData.lowestIntraDialyticBP &&
      !isNaN(Number(formData.lowestIntraDialyticBP))
    ) {
      const value = Number(formData.lowestIntraDialyticBP);
      if (value > 300)
        newErrors.lowestIntraDialyticBP = "Lowest BP seems too high";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: any) => {
    console.log("Form submission triggered", formData);
    e.preventDefault();

    if (validateForm()) {
      try {
        const sessionData = { ...formData };
        console.log("Session Data:", sessionData);

        // Call the API to store session data
        const response = await createApiClient().postSessionData(
          sessionData,
          2925
        );

        console.log("API Response:", response);
        alert("Session data saved successfully!");
      } catch (error) {
        console.error("Error saving session data:", error);
        alert("Failed to save session data. Please try again.");
      }
    }
  };

  const mappedData = data?.map((item: any) => ({
    date: moment(item.run_date).format("MMM DD, YYYY"),
    pre_wt: item.avg_post_wt_kg,
    post_wt: item.post_wt_kg,
    tw: item.target_weight,
    pre_bp: item.pre_bp_systolic,
    post_bp: item.post_bp_systolic,
    lowest_bp: item.lowest_bp_systolic,
    duration: item.duration_in_hours * 60,
    recommmedation: item.recommmedation,
    status: "Accepted" // or logic based on your condition
  }));

  const fields = [
    { key: "preWeight", label: "Pre-weight (kg)", step: 0.1 },
    { key: "postWeight", label: "Post-weight (kg)", step: 0.1 },
    { key: "targetWeight", label: "Target weight (kg)", step: 0.1 },
    { key: "preHdSystolicBP", label: "Pre-HD Systolic BP" },
    { key: "postHdSystolicBP", label: "Post-HD Systolic BP" },
    { key: "lowestIntraDialyticBP", label: "Lowest Intra-dialytic BP" },
    { key: "duration", label: "Duration (minutes)" },
    { key: "preBpSystolic", label: "Pre-BP Systolic" },
    { key: "preBpDiastolic", label: "Pre-BP Diastolic" },
    { key: "postBpSystolic", label: "Post-BP Systolic" },
    { key: "postBpDiastolic", label: "Post-BP Diastolic" },
    {
      key: "interDiaWtGain",
      label: "Inter-dialytic Weight Gain (kg)",
      step: 0.1
    },
    { key: "idwgPercentEhr", label: "IDWG Percent EHR", step: 0.1 }
  ];

  // Deny modal handlers
  const handleDenyClick = () => {
    setIsDenyModalVisible(true);
  };

  const handleDenyModalCancel = () => {
    setIsDenyModalVisible(false);
    // Reset form state
    setDenialReasons({
      lowBP: false,
      highBP: false,
      priorOrCurrentSymptoms: false,
      lowBPSymptoms: false,
      cramping: false,
      nausea: false,
      volumeOverload: false,
      swellingPuffiness: false,
      shortnessOfBreath: false,
      excessiveIDWG: false,
      patientRefusal: false,
      otherClinicalJudgement: false
    });
    setCustomReasonText("");
  };

  const handleDenialReasonChange = (reason: string) => {
    setDenialReasons((prev: any) => ({
      ...prev,
      [reason]: !prev[reason]
    }));
  };

  const handleSubmitDenial = () => {
    // Handle the denial submission here
    console.log("Denial reasons:", denialReasons);
    console.log("Custom reason:", customReasonText);

    // You can add API call here to submit the denial

    // Close modal after submission
    setIsDenyModalVisible(false);

    // Reset form state
    setDenialReasons({
      lowBP: false,
      highBP: false,
      priorOrCurrentSymptoms: false,
      lowBPSymptoms: false,
      cramping: false,
      nausea: false,
      volumeOverload: false,
      swellingPuffiness: false,
      shortnessOfBreath: false,
      excessiveIDWG: false,
      patientRefusal: false,
      otherClinicalJudgement: false
    });
    setCustomReasonText("");
  };

  return (
    <>
      {modalData?.data.length > 0 && (
        <AppModal
          visible={modalData?.data.length > 0 ? true : false}
          title={
            modalData ? (
              <div>
                {modalData.data.length > 2 ? (
                  <div>
                    {modalData.title}{" "}
                    <span
                      style={{
                        fontSize: "14px",
                        color: Colors.Grey,
                        marginLeft: "10px"
                      }}
                    >
                      [
                      {Math.round(
                        modalData.data[2].data[
                          modalData.data[2].data.length - 1
                        ][1]
                      )}{" "}
                      -{" "}
                      {Math.round(
                        modalData.data[3].data[
                          modalData.data[2].data.length - 1
                        ][1]
                      )}
                      ]
                    </span>
                  </div>
                ) : (
                  <div>{modalData.title}</div>
                )}
              </div>
            ) : (
              ""
            )
          }
          onCancel={() => setModalData({ data: [], title: "", colors: [] })}
          width={"80%"}
          footer={false}
        >
          <AppLineChart
            chartHeight="40%"
            multiLineChart
            seriesData={modalData ? [modalData.data[0], modalData.data[1]] : []}
            showLegend
            useTimestampforXaxis
            customColors={modalData.colors}
            customSeriesTooltip
          />
        </AppModal>
      )}

      {/* Deny Modal */}
      <Modal
        title="Treatment Recommendation"
        visible={isDenyModalVisible}
        onCancel={handleDenyModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDenyModalCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmitDenial}
            style={{ backgroundColor: Colors.Red, borderColor: Colors.Red }}
          >
            Submit Denial
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: "16px" }}>
          <strong>
            Shortness of breath (during HD or in between HD sessions)
          </strong>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ marginBottom: "8px" }}>
            <strong>Reason for Denial</strong>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Checkbox
              style={{ marginLeft: 8 }}
              checked={denialReasons.lowBP}
              onChange={() => handleDenialReasonChange("lowBP")}
            >
              Low BP
            </Checkbox>

            <Checkbox
              checked={denialReasons.highBP}
              onChange={() => handleDenialReasonChange("highBP")}
            >
              High Blood Pressure
            </Checkbox>

            <Checkbox
              checked={denialReasons.priorOrCurrentSymptoms}
              onChange={() =>
                handleDenialReasonChange("priorOrCurrentSymptoms")
              }
            >
              Prior or current symptoms
            </Checkbox>

            {denialReasons.priorOrCurrentSymptoms && (
              <div
                style={{
                  marginLeft: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px"
                }}
              >
                <Checkbox
                  style={{ marginLeft: 8 }}
                  checked={denialReasons.lowBPSymptoms}
                  onChange={() => handleDenialReasonChange("lowBPSymptoms")}
                >
                  Low BP related (dizziness, passing out, unwell or chest pain)
                </Checkbox>
                <Checkbox
                  checked={denialReasons.cramping}
                  onChange={() => handleDenialReasonChange("cramping")}
                >
                  Cramping
                </Checkbox>
                <Checkbox
                  checked={denialReasons.nausea}
                  onChange={() => handleDenialReasonChange("nausea")}
                >
                  Nausea
                </Checkbox>
              </div>
            )}

            <Checkbox
              checked={denialReasons.volumeOverload}
              onChange={() => handleDenialReasonChange("volumeOverload")}
            >
              Volume overload
            </Checkbox>

            {denialReasons.volumeOverload && (
              <div
                style={{
                  marginLeft: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px"
                }}
              >
                <Checkbox
                  style={{ marginLeft: 8 }}
                  checked={denialReasons.swellingPuffiness}
                  onChange={() => handleDenialReasonChange("swellingPuffiness")}
                >
                  Swelling/puffiness
                </Checkbox>
                <Checkbox
                  checked={denialReasons.shortnessOfBreath}
                  onChange={() => handleDenialReasonChange("shortnessOfBreath")}
                >
                  Shortness of breath
                </Checkbox>
                <Checkbox
                  checked={denialReasons.excessiveIDWG}
                  onChange={() => handleDenialReasonChange("excessiveIDWG")}
                >
                  Excessive IDWG
                </Checkbox>
              </div>
            )}

            <Checkbox
              checked={denialReasons.patientRefusal}
              onChange={() => handleDenialReasonChange("patientRefusal")}
            >
              Patient refusal
            </Checkbox>

            <Checkbox
              checked={denialReasons.otherClinicalJudgement}
              onChange={() =>
                handleDenialReasonChange("otherClinicalJudgement")
              }
            >
              Other clinical judgement
            </Checkbox>

            {denialReasons.otherClinicalJudgement && (
              <div style={{ marginLeft: "24px", marginTop: "8px" }}>
                <input
                  type="text"
                  placeholder="Brief description"
                  value={customReasonText}
                  onChange={e => setCustomReasonText(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px"
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>

      <CommonPatientExtensionItems />
      <FluidMetricsTitleText>Fluid Metrics</FluidMetricsTitleText>

      <div
        style={{
          padding: "0px",
          minHeight: "100vh",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        {/* Main Form Card */}
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            padding: "24px"
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "12px",
              color: "#1f2937",
              margin: "0 0 12px 0"
            }}
          >
            Dialysis Session Entry
          </h3>

          <Form layout="vertical">
            <Row gutter={[8, 2]}>
              {fields.map((field: any) => (
                <Col xs={24} sm={12} md={8} lg={6} key={field.key}>
                  <Form.Item
                    label={field.label}
                    validateStatus={errors[field.key] ? "error" : ""}
                    help={errors[field.key]}
                    style={{
                      marginBottom: 16,
                      fontWeight: "",
                      paddingRight: "10px"
                    }}
                  >
                    <span style={{ fontWeight: "600", fontSize: 16 }}>
                      {formData[field.key]}
                    </span>
                    {/* <InputNumber
                      disabled={true}
                      style={{ width: "100%", borderRadius: "8px" }}
                      step={field.step || 1}
                      value={formData[field.key]}
                      onChange={value => handleInputChange(field.key, value)}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    /> */}
                  </Form.Item>
                </Col>
              ))}
            </Row>
            <Button
              type={"primary"}
              style={{ borderRadius: "8px", float: "right", marginTop: -20 }}
            >
              Clinical Assessment
            </Button>
          </Form>
        </div>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            padding: "24px"
          }}
        >
          {/* <Button type="primary" style={{ borderRadius: "8px" }}>
            Generate Recommendation
          </Button> */}

          <div
            style={{
              display: "flex",
              gap: 20,
              justifyItems: "center",
              alignItems: "center"
            }}
          >
            <div style={{ fontWeight: "semibold", fontSize: "16px" }}>
              <span style={{ fontWeight: "bold" }}>
                Assess clinically for : Target Weight 115.5 Kg
              </span>
            </div>
            {/* <div>
              {" "}
              <Button type="primary" style={{ borderRadius: "8px" }}>
                Accept
              </Button>{" "}
              <Button
                type="default"
                onClick={handleDenyClick}
                style={{
                  backgroundColor: Colors.Red,
                  color: "white",
                  borderRadius: "8px"
                }}
              >
                Deny
              </Button>
            </div> */}
          </div>
        </div>

        {/* Rest of your existing content */}
        <FluidMetricsGridContainer>
          {countData.map((item, index) => (
            <FluidMetricsGridItem key={index}>
              <AppCard cardHeight="100%" cardWidth="100%">
                <CountItemTitleContainer>{item.title}</CountItemTitleContainer>
                {loading ? (
                  <Skeleton
                    loading={loading}
                    active={loading}
                    avatar={false}
                    paragraph={{ rows: 1 }}
                  />
                ) : (
                  <>
                    <CountItemCountText>{item.count}</CountItemCountText>
                    <CountItemDescriptionContainer title={item.description}>
                      {item.description}
                    </CountItemDescriptionContainer>
                  </>
                )}
              </AppCard>
            </FluidMetricsGridItem>
          ))}

          {/* Rest of your chart components */}
          <FluidMetricsResponsiveGridItem rowSpan={2} colSpan={2}>
            <AppCard
              cardHeight="100%"
              cardWidth="100%"
              title={
                <CardTitleText>
                  Blood Pressure Trends{" "}
                  <ExpandAltOutlined
                    style={{
                      float: "right",
                      fontSize: "20px",
                      color: Colors.DodgerBlue,
                      cursor: "pointer"
                    }}
                    onClick={() =>
                      showModal(
                        "Blood Pressure Trend",
                        bloodPressureTrendsChartData,
                        [
                          Colors.DodgerBlue, // Pre-HD Systolic
                          Colors.Red, // Post-HD Systolic
                          Colors.LightGrey // Lowest Intra-dialytic
                        ]
                      )
                    }
                  />
                </CardTitleText>
              }
            >
              {loading ? (
                <Spin
                  style={{
                    display: "grid",
                    placeItems: "center",
                    height: "70%"
                  }}
                  spinning={loading}
                />
              ) : (
                <AppLineChart
                  chartHeight="65%"
                  multiLineChart
                  seriesData={bloodPressureTrendsChartData}
                  showLegend
                  useTimestampforXaxis
                  customColors={[
                    Colors.DodgerBlue, // Pre-HD Systolic
                    Colors.Red, // Post-HD Systolic
                    Colors.LightGrey // Lowest Intra-dialytic
                  ]}
                  chartMarginRight={50}
                  customSeriesTooltip
                />
              )}
            </AppCard>
          </FluidMetricsResponsiveGridItem>

          {/* Other chart components remain the same */}
          <FluidMetricsGridItem rowSpan={2} colSpan={4}>
            <AppCard
              cardHeight="100%"
              cardWidth="100%"
              title={
                <CardTitleText>
                  Target weight vs Post weight{" "}
                  <ExpandAltOutlined
                    style={{
                      float: "right",
                      fontSize: "20px",
                      color: Colors.DodgerBlue,
                      cursor: "pointer"
                    }}
                    onClick={() =>
                      showModal(
                        "Target weight vs Post weight",
                        weightLineChartData,
                        [Colors.DodgerBlue, Colors.Red]
                      )
                    }
                  />
                </CardTitleText>
              }
            >
              {loading ? (
                <Spin
                  style={{
                    display: "grid",
                    placeItems: "center",
                    height: "70%"
                  }}
                  spinning={loading}
                />
              ) : (
                <AppLineChart
                  chartHeight="35%"
                  multiLineChart
                  seriesData={weightLineChartData}
                  showLegend
                  useTimestampforXaxis
                  customColors={[Colors.DodgerBlue, Colors.Red]}
                  customSeriesTooltip
                />
              )}
            </AppCard>
          </FluidMetricsGridItem>

          <FluidMetricsResponsiveGridItem rowSpan={2} colSpan={2}>
            <AppCard
              cardHeight="100%"
              cardWidth="100%"
              title={
                <CardTitleText>
                  Actual UFR{" "}
                  <ExpandAltOutlined
                    style={{
                      float: "right",
                      fontSize: "20px",
                      color: Colors.DodgerBlue,
                      cursor: "pointer"
                    }}
                    onClick={() =>
                      showModal("Actual UFR", actualUFRLineChartData, [
                        Colors.Red,
                        Colors.DodgerBlue
                      ])
                    }
                  />
                </CardTitleText>
              }
            >
              {loading ? (
                <Spin
                  style={{
                    display: "grid",
                    placeItems: "center",
                    height: "70%"
                  }}
                  spinning={loading}
                />
              ) : (
                <AppLineChart
                  chartHeight="65%"
                  multiLineChart
                  seriesData={actualUFRLineChartData}
                  showLegend
                  useTimestampforXaxis
                  customColors={[Colors.Red, Colors.DodgerBlue]}
                  customSeriesTooltip
                />
              )}
            </AppCard>
          </FluidMetricsResponsiveGridItem>
        </FluidMetricsGridContainer>

        <div
          style={{
            marginTop: 20,
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            padding: "24px"
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "12px",
              color: "#1f2937",
              margin: "0 0 12px 0"
            }}
          >
            Dialysis History
          </h3>
          <FluidMetricsTable
            data={mappedData}
            loading={loading}
            error={error}
            pagination={{
              pageSize: 10,
              current: 1,
              total: data?.length ?? 0
            }}
          />
        </div>
      </div>
    </>
  );
};
